import { create } from "zustand";

// Impersonation context for platform admins. Set by useImpersonationFromUrl
// when ?as_org=<uuid> is in the URL or persisted in localStorage.
//
// `activeOrg` holds the full org details for the impersonated org —
// id, name, slug, is_service_provider. Loaded asynchronously from
// /api/v1/orgs/active (which respects the X-Active-Org header that
// fetchWithAuth sends after impersonation activates). Before the fetch
// resolves, activeOrg is null but activeOrgId/activeOrgName are
// already set (the basic id+name come from demoOrgs.js immediately).
//
// Consumers needing is_service_provider should use useEffectiveOrg —
// it returns this store's activeOrg when impersonating, else
// useOrgStore.activeOrg. The brief pending state where activeOrg
// is null fails closed (button hidden, gate denies) which is the
// correct UX — we don't want to flash the "+ New case" button for
// half a second while the impersonated org's flag loads.

// ── THE STORAGE KEY LIVES HERE, AND IT IS sessionStorage ─────────────────────
//
// ONE HOME, BECAUSE IT HAD TWO. `debug.as_org` was a `const LS_KEY` in
// useImpersonationFromUrl.js and a bare string literal in
// ImpersonationBanner.jsx's handleClear, with a third mention in
// persistKeys.js's comment. Three copies of one name, correct on the day each
// was written and with nothing reporting the day they part — REM-249's shape.
// The name and both accessors are exported from here now; nothing else spells
// it.
//
// sessionStorage RATHER THAN localStorage, so impersonation is PER TAB and dies
// with the tab. Per-tab is the point rather than a side effect: holding
// DreamHammer in one tab and Earl in another is how entitlement gets verified,
// and that is impossible while the acting org is a single browser-wide value.
// Dying on close is the second half — an admin who forgets they are
// impersonating is the failure mode, and a tab that cannot outlive the session
// cannot carry the mistake into tomorrow.
//
// LicenseTermBanner.jsx:39 is the precedent and names itself "first use of
// sessionStorage in ui/src… it sidesteps the persistKeys contract". This is the
// second, and it sidesteps the same contract for a different reason: not to
// avoid the cross-user sweep but to get per-tab scope.
//
// ── WHAT THAT COSTS, AND WHAT HAD TO BE ADDED TO PAY IT ──────────────────────
//
// The cross-user sweep does NOT come along for free. enforceSessionBoundary()
// iterates localStorage only, and `debug.` is in SWEPT_KEY_PREFIXES, so today a
// different-user login wipes an impersonation left behind by the previous user.
// clearSessionState() is the sessionStorage sweep and it removed exactly one
// prefix, the licence banner's. Moving this key without widening that sweep
// would let a platform admin log out in a tab and hand the next platform admin
// their impersonation target. sessionGuard.js now sweeps this prefix too.
//
// ── THE localStorage KEY IS MIGRATED ONCE, NOT ABANDONED ─────────────────────
//
// A session impersonating at deploy time has `debug.as_org` in localStorage,
// which nothing would read again. Left alone it is a stale key that reads as
// live state to the next person who opens devtools, which is the same class of
// defect as the comment this commit corrects in main.jsx. readImpersonation()
// therefore adopts a localStorage value once and deletes it, so the impersonation
// survives the deploy in that tab and the key does not survive the read.

/** The one place this key is spelled. */
export const IMPERSONATION_KEY = "debug.as_org";

/**
 * The acting org from storage, adopting and clearing a pre-sessionStorage
 * localStorage value if one is present. Returns null when nothing is stored.
 */
export function readImpersonation() {
  let v = null;
  try { v = window.sessionStorage.getItem(IMPERSONATION_KEY); } catch { /* private mode */ }
  if (v) return v;
  try {
    const legacy = window.localStorage.getItem(IMPERSONATION_KEY);
    if (legacy) {
      window.localStorage.removeItem(IMPERSONATION_KEY);
      window.sessionStorage.setItem(IMPERSONATION_KEY, legacy);
      return legacy;
    }
  } catch { /* private mode, or storage blocked — impersonation simply ends */ }
  return null;
}

/** Persist, or clear when `orgId` is falsy. Never writes localStorage. */
export function writeImpersonation(orgId) {
  try {
    if (orgId) window.sessionStorage.setItem(IMPERSONATION_KEY, orgId);
    else window.sessionStorage.removeItem(IMPERSONATION_KEY);
  } catch { /* private mode — the store still holds it for this page */ }
}

export const usePlatformOrgContext = create(set => ({
  activeOrgId: null,
  activeOrgName: null,
  activeOrg: null,

  setActiveOrg: (org) =>
    set({
      activeOrgId: org?.id || null,
      activeOrgName: org?.name || null,
      // activeOrg gets set later by setActiveOrgDetails after the
      // /orgs/active fetch resolves; reset to null here so consumers
      // see a clean pending state on impersonation change.
      activeOrg: null,
    }),

  setActiveOrgDetails: (org) => set({ activeOrg: org || null }),

  clearActiveOrg: () =>
    set({
      activeOrgId: null,
      activeOrgName: null,
      activeOrg: null,
    }),
}));


// ── INVALIDATION ON A CHANGE OF ACTING ORG ───────────────────────────────────
//
// THE DEFECT THIS CLOSES. Nothing subscribed to this store — no `.subscribe(`
// on it anywhere in ui/src. main.jsx had two subscriptions on useOrgStore, and
// the second one's comment claimed to cover "impersonation, switching orgs
// mid-session". Impersonation never writes useOrgStore; it writes this one. So
// changing the acting org invalidated nothing.
//
// It was masked rather than absent. useImpersonationFromUrl is mount-only and
// impersonation is activated by pasting `?as_org=<uuid>`, which is a full page
// load: everything remounts and the question never arises. The Clear link on
// ImpersonationBanner is the path that does not get that for free — it calls
// clearActiveOrg() in place, and org-scoped data stayed on screen.
//
// WHY RELOAD RATHER THAN INVALIDATE STORES. Targeted invalidation is larger and
// INCOMPLETE BY CONSTRUCTION. Fifteen of the seventeen fetching stores hold
// org-scoped results — feature catalog, layer coverage, analytics products,
// AOIs, wellfinder queue, QC cases, projects, user layers, layer sets. Listing
// them is a hand-maintained mirror of a set nothing enforces: REM-249's shape,
// and exactly how main.jsx ended up with a subscription that had stopped
// describing what it watched.
//
// The stores are also not the whole surface. 45 of the 50 .jsx files calling
// fetchWithAuth keep the result in component-local useState, which no
// store-level invalidation can reach. A reload is the only complete answer, and
// it is complete without anyone maintaining a list.
//
// THE `prev` GUARD IS LOAD-BEARING — WITHOUT IT THIS RELOADS FOREVER. This
// store is not persisted, so it is null on every fresh page and restoration
// from localStorage is always null -> X. Reloading on that would restore the
// same value on the next page and fire again.
//
//   null -> X    restoration / activation   ignored, the page is already fresh
//   X    -> null Clear                      reload
//   X    -> Y    picker switch              reload
//
// `?as_org=clear` is safe under the same rule: on that path the store starts
// null and clearActiveOrg writes null, so nothing changes and nothing fires.
// Reloading there would loop on the URL before it is stripped.
//
// Injected rather than closed over so a test can drive real state transitions
// through the real store and observe the decision. Returns the unsubscribe.
export function installOrgContextInvalidation({ reload } = {}) {
  const doReload =
    reload || (() => { try { window.location.reload(); } catch { /* noop */ } });

  return usePlatformOrgContext.subscribe((state, prevState) => {
    if (state.activeOrgId === prevState.activeOrgId) return;
    if (!prevState.activeOrgId) return;
    doReload();
  });
}
